/**
 * Prematch / live event favorites — client-only, matching legacy SportsbookApp.
 * Prematch: sessionStorage `FavoritePrematch`. Live: localStorage `favLive`.
 * No UsersAPI persistence (that API only covers casino favoritegame).
 */

export const PREMATCH_FAVORITES_KEY = 'FavoritePrematch';
export const LIVE_FAVORITES_KEY = 'favLive';

export type SportsFavoriteMode = 'prematch' | 'live';

type Listener = () => void;

export type SportsFavoritesSnapshot = {
  prematchIds: number[];
  liveIds: number[];
  hasPrematchFavorites: boolean;
  version: number;
};

let version = 0;
let cachedSnapshot: SportsFavoritesSnapshot | null = null;
const listeners = new Set<Listener>();

function notify(): void {
  version += 1;
  cachedSnapshot = null;
  for (const listener of listeners) listener();
}

export function subscribeSportsFavorites(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSportsFavoritesVersion(): number {
  return version;
}

/**
 * Stable snapshot for `useSyncExternalStore` — same reference until `notify()`.
 * Returning a fresh object every call causes an infinite re-render loop.
 */
export function getSportsFavoritesSnapshot(): SportsFavoritesSnapshot {
  if (cachedSnapshot) return cachedSnapshot;
  const prematchIds = getPrematchFavoriteIds();
  const liveIds = getLiveFavoriteIds();
  cachedSnapshot = {
    prematchIds,
    liveIds,
    hasPrematchFavorites: prematchIds.length > 0,
    version,
  };
  return cachedSnapshot;
}

export function getSportsFavoritesServerSnapshot(): SportsFavoritesSnapshot {
  return EMPTY_SNAPSHOT;
}

const EMPTY_SNAPSHOT: SportsFavoritesSnapshot = {
  prematchIds: [],
  liveIds: [],
  hasPrematchFavorites: false,
  version: 0,
};

function readStorage(store: Storage | undefined, key: string): number[] {
  if (!store) return [];
  try {
    const raw = store.getItem(key);
    if (raw == null || raw === '') return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const ids: number[] = [];
    for (const value of parsed) {
      const id = typeof value === 'number' ? value : Number(value);
      if (Number.isFinite(id) && id > 0) ids.push(id);
    }
    return ids;
  } catch {
    return [];
  }
}

function writeStorage(store: Storage | undefined, key: string, ids: number[]): void {
  if (!store) return;
  if (ids.length === 0) {
    store.removeItem(key);
    return;
  }
  store.setItem(key, JSON.stringify(ids));
}

function sessionStore(): Storage | undefined {
  try {
    return typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
  } catch {
    return undefined;
  }
}

function localStore(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

export function getPrematchFavoriteIds(): number[] {
  return readStorage(sessionStore(), PREMATCH_FAVORITES_KEY);
}

export function getLiveFavoriteIds(): number[] {
  return readStorage(localStore(), LIVE_FAVORITES_KEY);
}

export function isPrematchFavorite(eventId: number): boolean {
  return getPrematchFavoriteIds().includes(eventId);
}

export function isLiveFavorite(eventId: number): boolean {
  return getLiveFavoriteIds().includes(eventId);
}

export function isSportsFavorite(eventId: number, mode: SportsFavoriteMode): boolean {
  return mode === 'live' ? isLiveFavorite(eventId) : isPrematchFavorite(eventId);
}

/** Toggle prematch favorite. Returns the new favorited state. */
export function togglePrematchFavorite(eventId: number): boolean {
  const id = Number(eventId);
  if (!Number.isFinite(id) || id <= 0) return false;

  const ids = getPrematchFavoriteIds();
  const index = ids.indexOf(id);
  let next: boolean;
  if (index >= 0) {
    ids.splice(index, 1);
    next = false;
  } else {
    ids.push(id);
    next = true;
  }
  writeStorage(sessionStore(), PREMATCH_FAVORITES_KEY, ids);
  notify();
  return next;
}

/** Toggle live favorite. Returns the new favorited state. */
export function toggleLiveFavorite(eventId: number, newState?: boolean): boolean {
  const id = Number(eventId);
  if (!Number.isFinite(id) || id <= 0) return false;

  const ids = getLiveFavoriteIds();
  const index = ids.indexOf(id);
  const isFav = index >= 0;
  let next: boolean;

  if (newState !== undefined) {
    if (newState && !isFav) ids.push(id);
    if (!newState && isFav) ids.splice(index, 1);
    next = newState;
  } else if (isFav) {
    ids.splice(index, 1);
    next = false;
  } else {
    ids.push(id);
    next = true;
  }

  writeStorage(localStore(), LIVE_FAVORITES_KEY, ids);
  notify();
  return next;
}

export function toggleSportsFavorite(eventId: number, mode: SportsFavoriteMode): boolean {
  return mode === 'live' ? toggleLiveFavorite(eventId) : togglePrematchFavorite(eventId);
}

/**
 * Drop live favorites that were previously visible in a feed and are now gone
 * (legacy RemoveTableRow clears favLive for that event). Does not touch IDs that
 * were never in `previouslySeen` (e.g. favorites from another sport).
 */
export function pruneLiveFavoritesGoneFromFeed(
  previouslySeenIds: ReadonlySet<number>,
  activeEventIds: ReadonlySet<number>,
): void {
  const ids = getLiveFavoriteIds();
  const next = ids.filter(
    (id) => !(previouslySeenIds.has(id) && !activeEventIds.has(id)),
  );
  if (next.length === ids.length) return;
  writeStorage(localStore(), LIVE_FAVORITES_KEY, next);
  notify();
}

/** Test helper — clears storage keys and resets the version bus. */
export function resetSportsFavoritesForTests(): void {
  sessionStore()?.removeItem(PREMATCH_FAVORITES_KEY);
  localStore()?.removeItem(LIVE_FAVORITES_KEY);
  version = 0;
  notify();
}
